import { ChangeEvent } from 'react';
import { ConditionalRender, UploadedFile } from '@ui';
import { CaseFile, CaseOrigin } from '@/api/apis/case';
import { RedactionConfirmationInstructions } from './RedactionConfirmationInstructions';
import { FilesUploadControl } from './FilesUploadControl';

const OPO_REDACTED_HINT_TEXT = 'This File was uploaded by the OPO and marked as redacted. Please review the document.';

export type FilesListProps = {
  files: CaseFile[];
  isUploading: boolean;
  onFileUpload: (e: ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: (fileId: string) => void;
  disabled?: boolean;
  showUploadBtn?: boolean;
  showDownloadBtn?: boolean;
  showDeleteBtn?: boolean;
  showPreviewBtn?: boolean;
  restrictRedactedFileDelete?: boolean;
  showRedactedMark?: boolean;
  hintLabel?: string;
  hintText?: string;
  requireRedactionConfirmation?: boolean;
  showRedactionInstructions?: boolean;
};

export const FilesList = ({
  files,
  isUploading,
  onFileUpload,
  onDeleteFile,
  disabled,
  showUploadBtn = true,
  showDownloadBtn = true,
  showDeleteBtn = true,
  showPreviewBtn = true,
  restrictRedactedFileDelete = false,
  showRedactedMark = true,
  hintLabel = 'Redacted',
  hintText = OPO_REDACTED_HINT_TEXT,
  requireRedactionConfirmation = false,
  showRedactionInstructions = false,
}: FilesListProps) => {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <ConditionalRender show={requireRedactionConfirmation || showRedactionInstructions}>
        <RedactionConfirmationInstructions />
      </ConditionalRender>

      <div className="flex-1 min-h-0 overflow-y-auto pr-3 flex flex-col gap-4 py-4">
        {!files.length && <div className="text-xl-sb text-base-20">No items</div>}
        {files.map((f) => {
          const isOpoFile = f.addedFromPlatform === CaseOrigin.Opo;
          const canDelete = showDeleteBtn && !(restrictRedactedFileDelete && isOpoFile);

          return (
            <UploadedFile
              key={f.id}
              file={f}
              onDelete={() => (f.id ? onDeleteFile(f.id) : null)}
              showDownloadBtn={showDownloadBtn}
              showDeleteBtn={canDelete}
              showHint={showRedactedMark && isOpoFile}
              hintText={hintText}
              hintLabel={hintLabel}
              showPreviewBtn={showPreviewBtn}
            />
          );
        })}
      </div>

      <ConditionalRender show={showUploadBtn}>
        <FilesUploadControl
          filesCount={files.length}
          isUploading={isUploading}
          onFileUpload={onFileUpload}
          disabled={disabled}
          requireRedactionConfirmation={requireRedactionConfirmation}
        />
      </ConditionalRender>
    </div>
  );
};
